import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotifications } from '../../hooks/useNotifications';
import { USER_ROLES } from '../../constants/authConstants';
import ProfileModal from '../profile/ProfileModal';

const Header = ({ onToggleSidebar, showMenuButton = true, sidebarCollapsed = false, activeTab = 'budget', onTabChange }) => {
  const { user, signOut, getUserDisplayName, getUserRole } = useAuth();
  const { theme, toggleTheme, isDark, isMobile, isTablet, isDesktop, getResponsiveValue } = useTheme();
  const { unreadCount } = useNotifications();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Sign out error:', error);
    }
  };

  const handleNavigation = (path) => {
    navigate(path);
    setShowUserMenu(false);
  };

  const handleOpenProfile = () => {
    setShowProfileModal(true);
    setShowUserMenu(false);
  };

  const handleTabClick = (tabId) => {
    onTabChange(tabId);
    setShowUserMenu(false);
  };

  const handleNotificationHistory = () => {
    navigate('/admin/notification-history');
  };

  const getRoleColor = (role) => {
    const colors = {
      admin: 'text-accent',
      planning_official: 'text-indigo-600',
      department: 'text-blue-600'
    };
    return colors[role] || 'text-text-secondary';
  };

  const getRoleLabel = (role) => {
    const labels = {
      admin: 'Administrator',
      planning_official: 'Planning Official',
      department: 'Department User'
    };
    return labels[role] || 'User';
  };

  const getInitials = (fullName) => {
    if (!fullName) return '?';
    const names = fullName.trim().split(' ');
    if (names.length === 1) return names[0].substring(0, 2).toUpperCase();
    return (names[0][0] + names[names.length - 1][0]).toUpperCase();
  };

  const isAdmin = getUserRole() === USER_ROLES.ADMIN;
  const isPlanningOfficial = getUserRole() === USER_ROLES.PLANNING_OFFICIAL;
  const isDoner = getUserRole() === USER_ROLES.DONER;
  // Roles with admin-style read access to management screens
  const hasAdminAccess = isAdmin || isPlanningOfficial || isDoner;
  const userRole = getUserRole();

  // Define all tabs with role restrictions
  const tabs = [
    { id: 'budget', label: 'Sectoral Outlay', allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.DEPARTMENT, USER_ROLES.PLANNING_OFFICIAL, USER_ROLES.DONER] },
    { id: 'state', label: 'State Plan Clearance', allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.PLANNING_OFFICIAL, USER_ROLES.DONER] },
    { id: 'central', label: 'Central Clearance', allowedRoles: [USER_ROLES.ADMIN, USER_ROLES.PLANNING_OFFICIAL, USER_ROLES.DONER] }
  ];

  // Filter tabs based on user role
  const availableTabs = tabs.filter(tab => tab.allowedRoles.includes(userRole));

  const headerPadding = getResponsiveValue({
    mobile: 'px-4 py-3',
    desktop: 'px-8 py-4'
  });

  const buttonSpacing = getResponsiveValue({
    mobile: 'space-x-2',
    tablet: 'space-x-4',
    desktop: 'space-x-6'
  });

  const avatarSize = getResponsiveValue({
    mobile: 'w-8 h-8',
    desktop: 'w-9 h-9'
  });

  const tabSpacing = getResponsiveValue({
    mobile: 'space-x-1',
    desktop: 'space-x-2'
  });

  const tabPadding = getResponsiveValue({
    mobile: 'px-3 py-2',
    desktop: 'px-4 py-2'
  });

  const tabTextSize = getResponsiveValue({
    mobile: 'text-sm',
    desktop: 'text-sm'
  });

  return (
    <>
      <header className={`bg-slate-600 dark:bg-slate-800 backdrop-blur-sm border-b border-slate-400 dark:border-slate-600 sticky top-0 z-50 ${headerPadding}`}>
        <div className="flex items-center justify-between">
          {/* Left Section - Menu + Tabs */}
          <div className="flex items-center space-x-4">
            {/* Hamburger Menu Button */}
            {showMenuButton && (
              <button 
                onClick={onToggleSidebar}
                className="p-2 text-slate-200 hover:text-white hover:bg-slate-700 dark:hover:bg-slate-900 rounded-lg transition-all duration-200"
                title={sidebarCollapsed ? 'Open sidebar' : 'Close sidebar'}
              >
                {isMobile ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : sidebarCollapsed ? (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 19l-7-7 7-7" />
                  </svg>
                )}
              </button>
            )}

            {/* Navigation Tabs */}
            {availableTabs.length > 0 && (
              <nav className={`flex ${tabSpacing}`}>
                {availableTabs.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => handleTabClick(tab.id)}
                    className={`
                      ${tabPadding} ${tabTextSize} font-medium rounded-md transition-all duration-200
                      ${activeTab === tab.id 
                        ? 'bg-slate-700 dark:bg-slate-900 text-white shadow-lg' 
                        : 'text-slate-200 hover:text-white hover:bg-slate-700 dark:hover:bg-slate-900'
                      }
                      ${isMobile ? 'min-w-[60px]' : 'min-w-[80px]'}
                    `}
                  >
                    {tab.label}
                  </button>
                ))}
              </nav>
            )}
          </div>

          {/* Right Section */}
          <div className={`flex items-center ${buttonSpacing}`}>
            
            {/* Notification History - Admin & Planning Official Only */}
            {hasAdminAccess && (
              <button 
                onClick={handleNotificationHistory}
                className="p-2 text-slate-200 hover:text-white hover:bg-slate-700 dark:hover:bg-slate-900 rounded-lg transition-all duration-200"
                title="Notification History"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </button>
            )}

            {/* Notification Bell */}
            <div className="relative">
              <button 
                onClick={() => handleNavigation('/notifications')}
                className="p-2 text-slate-200 hover:text-white hover:bg-slate-700 dark:hover:bg-slate-900 rounded-lg transition-all duration-200 relative"
                title="Notifications"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={1.5} 
                    d="M15 17h5l-5 5V12a1 1 0 00-1-1h-1m-1 1v5a2 2 0 01-2 2H6a2 2 0 01-2-2V7a2 2 0 012-2h6m2 0V4a1 1 0 011-1h1a1 1 0 011 1v1m0 0h2"
                  />
                </svg>
                
                {unreadCount > 0 && (
                  <div className="absolute -top-1 -right-1 bg-notification-badge text-notification-badge-text text-xs font-medium rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </div>
                )}
              </button>
            </div>

            {/* User Profile */}
            <div className="relative">
              <div 
                className={`flex items-center cursor-pointer ${
                  isMobile ? 'space-x-2' : 'space-x-3 pl-4 border-l border-slate-400 dark:border-slate-600'
                }`}
                onClick={() => setShowUserMenu(!showUserMenu)}
              >
                {/* User Info - Hide text on mobile */}
                {!isMobile && (
                  <div className="text-right">
                    <div className="text-sm font-medium text-white">
                      {getUserDisplayName()}
                    </div>
                    <div className={`text-xs ${getRoleColor(getUserRole())}`}>
                      {getRoleLabel(getUserRole())}
                    </div>
                  </div>
                )}
                
                {/* Avatar */}
                <div className="relative">
                  {user?.profilePicture ? (
                    <img 
                      src={user.profilePicture} 
                      alt={getUserDisplayName()}
                      className={`${avatarSize} rounded-xl object-cover shadow-theme-sm border-2 border-slate-400`}
                    />
                  ) : (
                    <div className={`${avatarSize} bg-slate-700 rounded-xl flex items-center justify-center shadow-theme-sm`}>
                      <span className="text-white text-sm font-medium">
                        {getInitials(user?.fullName)}
                      </span>
                    </div>
                  )}
                  
                  {/* Online Status */}
                  <div className={`absolute -bottom-0.5 -right-0.5 ${
                    isMobile ? 'w-2.5 h-2.5' : 'w-3 h-3'
                  } bg-success border-2 border-slate-600 rounded-full`}></div>
                </div>

                {/* Dropdown Arrow - Hide on mobile */}
                {!isMobile && (
                  <svg 
                    className={`w-4 h-4 text-slate-200 transition-transform ${showUserMenu ? 'rotate-180' : ''}`}
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 9l-7 7-7-7" />
                  </svg>
                )}
              </div>

              {/* User Dropdown Menu */}
              {showUserMenu && (
                <div className={`absolute right-0 mt-2 bg-surface-elevated rounded-xl shadow-theme-lg border border-border-primary py-2 z-[60] ${
                  isMobile ? 'w-64' : 'w-72'
                }`}>
                  {/* User Info Header */}
                  <div className="px-4 py-3 border-b border-border-primary">
                    <div className="flex items-center space-x-3">
                      <div className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} bg-navigation rounded-xl flex items-center justify-center`}>
                        {user?.profilePicture ? (
                          <img 
                            src={user.profilePicture} 
                            alt={getUserDisplayName()}
                            className={`${isMobile ? 'w-10 h-10' : 'w-12 h-12'} rounded-xl object-cover`}
                          />
                        ) : (
                          <span className="text-navigation-text text-base font-semibold">
                            {getInitials(user?.fullName)}
                          </span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-text-primary truncate">{getUserDisplayName()}</div>
                        <div className="text-sm text-text-secondary truncate">{user?.email}</div>
                        <div className="flex items-center space-x-2 mt-1">
                          <span className={`text-xs ${getRoleColor(getUserRole())}`}>
                            {getRoleLabel(getUserRole())}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Menu Items */}
                  <div className="py-1">
                    {/* View Profile */}
                    <button 
                      onClick={handleOpenProfile}
                      className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-hover flex items-center space-x-3 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" 
                        />
                      </svg>
                      <span>View Profile</span>
                    </button>

                    {/* User Management - Admin & Planning Official */}
                    {hasAdminAccess && (
                      <button 
                        onClick={() => handleNavigation('/admin/users')}
                        className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-hover flex items-center space-x-3 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                            d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" 
                          />
                        </svg>
                        <span>User Management</span>
                      </button>
                    )}

                    {/* Department Management - Admin & Planning Official */}
                    {hasAdminAccess && (
                      <button 
                        onClick={() => handleNavigation('/admin/departments')}
                        className="w-full text-left px-4 py-3 text-sm text-text-primary hover:bg-surface-hover flex items-center space-x-3 transition-colors duration-200"
                      >
                        <svg className="w-4 h-4 text-text-tertiary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                            d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" 
                          />
                        </svg>
                        <span>Department Management</span>
                      </button>
                    )}
                  </div>

                  {/* Sign Out */}
                  <div className="border-t border-border-primary pt-1">
                    <button 
                      onClick={handleSignOut}
                      className="w-full text-left px-4 py-3 text-sm text-error hover:bg-error-subtle flex items-center space-x-3 transition-colors duration-200"
                    >
                      <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} 
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" 
                        />
                      </svg>
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Click outside to close menu */}
        {showUserMenu && (
          <div 
            className="fixed inset-0 z-[55]" 
            onClick={() => setShowUserMenu(false)}
          />
        )}
      </header>

      {/* Profile Modal */}
      <ProfileModal 
        isOpen={showProfileModal}
        onClose={() => setShowProfileModal(false)}
      />
    </>
  );
};

export default Header;